import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, styled, Typography } from '@mui/material';
import { toast } from '@side/pantry';
import { useCallback, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { AllowedImageFileExtensions } from 'src/types';
import { getAcceptedDropzoneFiles } from './helpers/dropZoneAccept';
import { ImagePreview } from './ImagePreview';
import { initialValidationState } from './initialState';
import { UploadImageModalContext } from './UploadImageModalContext';

export const invalidFileSizeErrorMsg =
  'File size too large, try a smaller than 800kb file';
export const invalidImageDimensionsErrorMsg =
  'Image must be at least 600px wide and 600px tall';
export const invalidImageErrorMsg = 'Please upload an image file';

const minimumImageDimensions = 600;
export const graphQLMaxPayloadLimit = 800; // in kilobytes

export type UploadFormContentProps = {
  uploading?: boolean;
  selectedFile: File | null;
  fileValidation: typeof initialValidationState;
  setSelectedFile: (file: File | null) => void;
  setFileValidation: (fileValidation: typeof initialValidationState) => void;
};

const ImageBackground = styled(Box)(({ theme }) => ({
  border: `1.5px dashed ${theme.palette.other.outlineBorder}`,
  width: '100%',
  position: 'relative',
  borderRadius: '4px',
  display: 'flex',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  '&:hover': { backgroundColor: theme.palette.action.hover },
  '&[aria-disabled="true"]': {
    pointerEvents: 'none',
    filter: 'grayscale(1)',
    opacity: 0.65,
  },
  height: '250px',
  [`${theme.breakpoints.down('sm')}`]: {
    height: '170px',
  },
}));

export const isValidImageSize = (base64Image: string | null) => {
  if (!base64Image) return undefined;
  const stringLength = base64Image.length;
  const sizeInKiloBytes = (Math.ceil(stringLength / 4) * 3) / 1024;
  return sizeInKiloBytes <= graphQLMaxPayloadLimit;
};

export const isValidImageExtension = (path?: string) => {
  if (!path) {
    return false;
  }
  const validExtensions = Object.values(AllowedImageFileExtensions);
  const extension: AllowedImageFileExtensions | 'unknown_file_extension' =
    (path?.split('.').pop()?.toLowerCase() as AllowedImageFileExtensions) ??
    'unknown_file_extension';

  return validExtensions.includes(extension);
};

export const UploadFormContent = ({
  uploading,
  selectedFile,
  fileValidation,
  setSelectedFile,
  setFileValidation,
}: UploadFormContentProps) => {
  const { clearErrors, setError, setValue, register } = useFormContext();

  const [, dispatch] = useContext(UploadImageModalContext);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      clearErrors();
      setFileValidation(initialValidationState);

      const file = acceptedFiles[0];
      const filePath = file?.name;

      if (!file || !isValidImageExtension(filePath)) {
        setFileValidation({ extension: invalidImageErrorMsg });
        setError('imageFile', { message: invalidImageErrorMsg });
        toast('error', invalidImageErrorMsg);
        return;
      }

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!isValidImageSize(reader.result as string)) {
            setFileValidation({ size: invalidFileSizeErrorMsg });
            setError('imageFile', { message: invalidFileSizeErrorMsg });
            toast('error', invalidFileSizeErrorMsg);
            return;
          }

          const image = new Image();
          image.src = reader.result as string;
          image.onload = () => {
            if (
              image.width <= minimumImageDimensions ||
              image.height <= minimumImageDimensions
            ) {
              setFileValidation({ dimension: invalidImageDimensionsErrorMsg });
              setError('imageFile', {
                message: invalidImageDimensionsErrorMsg,
              });
              toast('error', invalidImageDimensionsErrorMsg);
            }
          };

          setSelectedFile(file);
          dispatch({
            type: 'SET_BASE64_IMAGE',
            payload: reader.result as string,
          });
          dispatch({ type: 'ENABLE_CROP', payload: true });
          dispatch({ type: 'SET_BASE64_CROPPED_IMAGE', payload: null });
          setValue('imageFile', file.name, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        };

        reader.readAsDataURL(file);
      }
    },
    [
      setError,
      setFileValidation,
      setSelectedFile,
      clearErrors,
      setValue,
      dispatch,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    disabled: uploading,
    maxFiles: 1,
    accept: getAcceptedDropzoneFiles(),
  });

  const handleRemoveImage = () => {
    dispatch({ type: 'REMOVE_IMAGE' });
    setSelectedFile(null);
    clearErrors();
    setFileValidation(initialValidationState);
    setValue('imageFile', null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const imagePreviewProps = {
    uploading,
    selectedFile,
    fileValidation,
    setFileValidation,
    handleRemoveImage,
  };

  return (
    <>
      <ImageBackground
        {...getRootProps()}
        sx={(theme) => ({
          px: {
            xs: theme.spacing(1),
            sm: theme.spacing(1),
            md: theme.spacing(4),
          },
          py: {
            xs: theme.spacing(1),
            sm: theme.spacing(1),
            md: theme.spacing(2),
          },
        })}
      >
        <input {...register('imageFile')} {...getInputProps()} id="dropzone" />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CloudUploadIcon
            color="primary"
            sx={{
              transition: 'color 200ms',
              fontSize: 32,
              mt: { xs: 1, md: 2 },
            }}
          />
          <Typography variant="h4" component="span" ml={1} pb={1}>
            Drag &amp; drop your files, or{' '}
            <Typography
              variant="h4"
              component="span"
              sx={{
                color: (t) => t.palette.primary.main,
                textDecoration: 'underline',
              }}
            >
              browse
            </Typography>
          </Typography>
          <Typography
            variant="body3"
            sx={{
              mt: 1,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            Supported file type:{' '}
            {Object.values(AllowedImageFileExtensions).join(', ')} smaller than{' '}
            <strong>800 KB</strong>
          </Typography>
        </Box>
      </ImageBackground>
      <ImagePreview {...imagePreviewProps} />
    </>
  );
};
